import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import logoText from "@assets/images/logo/dark-logo-text-nobg.png";

/**
 * ExtensionAuth - Handles authentication for the browser extension
 *
 * This component handles two cases:
 * 1. User is already logged in -> Get token and send to extension
 * 2. User is not logged in -> Redirect to login with extension params
 */
const ExtensionAuth = () => {
  const location = useLocation();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [status, setStatus] = useState("checking"); // checking, sending, success, error

  useEffect(() => {
    const handleAuth = async () => {
      try {
        // Get the access token from cookie - cookies are shared across domain
        const accessToken = Cookies.get("access");
        console.log("ExtensionAuth: Access token:", accessToken ? "exists" : "none");
        console.log("ExtensionAuth: User from Redux:", user);

        // Check if user is logged in (has access token)
        // In popup window, Redux might not have user data, but cookie will exist
        if (accessToken) {
          console.log("ExtensionAuth: User has access token, sending to extension");
          setStatus("sending");

          // User is already logged in - send token to extension
          if (window.opener) {
            try {
              // Send success message to the extension
              // Include user data if available from Redux, otherwise just send token
              window.opener.postMessage({
                type: "GOOGLE_AUTH_SUCCESS",
                payload: {
                  accessToken: accessToken,
                  user: user ? {
                    _id: user._id,
                    name: user.name,
                    email: user.email,
                    currentPlan: user.currentPlan,
                    subscriptionStatus: user.subscriptionStatus,
                  } : null,
                }
              }, "*");

              setStatus("success");

              // Auto-close after 2 seconds
              setTimeout(() => {
                window.close();
              }, 2000);
            } catch (error) {
              console.error("Error sending message to extension:", error);
              setStatus("error");
            }
          } else {
            // No opener - redirect to success page with token
            const userData = user ? encodeURIComponent(JSON.stringify({
              _id: user._id,
              name: user.name,
              email: user.email,
              currentPlan: user.currentPlan,
              subscriptionStatus: user.subscriptionStatus,
            })) : "";
            window.location.href = `/extension/auth/success?token=${accessToken}${userData ? `&user=${userData}` : ""}`;
          }
        } else {
          console.log("ExtensionAuth: No access token, redirecting to login");
          // User is not logged in - redirect to login with extension params
          const searchParams = new URLSearchParams(location.search);
          const oauth = searchParams.get("oauth");

          if (oauth === "google") {
            // Redirect to login page for Google OAuth
            history.replace("/login?source=extension&oauth=google");
          } else {
            // Just redirect to login
            history.replace("/login?source=extension");
          }
        }
      } catch (error) {
        console.error("ExtensionAuth: Error in handleAuth", error);
        setStatus("error");
      }
    };

    // Small delay to ensure component is mounted
    const timer = setTimeout(() => {
      handleAuth();
    }, 100);

    return () => clearTimeout(timer);
  }, [user, location, history]);

  const handleCloseManually = () => {
    window.close();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        fontFamily: "'Nunito', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          padding: "40px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
          textAlign: "center",
          maxWidth: "400px",
          width: "100%",
        }}
      >
        <img
          src={logoText}
          alt="Copilotly"
          style={{
            height: "40px",
            marginBottom: "24px",
          }}
        />

        {status === "checking" && (
          <>
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "4px solid #e5e7eb",
                borderTop: "4px solid #8b5cf6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <style>
              {`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}
            </style>
            <h2 style={{ color: "#374151", marginBottom: "8px" }}>
              Checking Authentication...
            </h2>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              Please wait while we verify your login status.
            </p>
          </>
        )}

        {status === "sending" && (
          <>
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "4px solid #e5e7eb",
                borderTop: "4px solid #8b5cf6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <h2 style={{ color: "#374151", marginBottom: "8px" }}>
              Connecting to Extension...
            </h2>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              Sending your credentials to Copilotly extension.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #10b981, #059669)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2
              style={{
                color: "#059669",
                marginBottom: "8px",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              Already Logged In!
            </h2>

            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            >
              Your session has been connected to the extension.
            </p>

            <p
              style={{
                color: "#9ca3af",
                fontSize: "13px",
                marginBottom: "16px",
              }}
            >
              This window will close automatically...
            </p>

            <button
              onClick={handleCloseManually}
              style={{
                background: "linear-gradient(to right, #2e32ed, #8b5cf6, #e80b78)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "opacity 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.opacity = "0.9")}
              onMouseOut={(e) => (e.target.style.opacity = "1")}
            >
              Close Window
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dc2626"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>

            <h2
              style={{
                color: "#dc2626",
                marginBottom: "8px",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              Something went wrong
            </h2>

            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            >
              Could not connect to the extension. Please try again.
            </p>

            <button
              onClick={handleCloseManually}
              style={{
                background: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Close Window
            </button>
          </>
        )}
      </div>

      <p
        style={{
          color: "#9ca3af",
          fontSize: "12px",
          marginTop: "24px",
        }}
      >
        © {new Date().getFullYear()} Copilotly™ - Your Personal AI Copilot
      </p>
    </div>
  );
};

export default ExtensionAuth;
